import { Card, CardContent } from './ui/card'
import { Badge } from './ui/badge'
import { Plane, Cpu, ShieldCheck, Warehouse, DoorOpen, Ship } from 'lucide-react'
import { useEffect, useRef } from 'react'
import ctaBg from '../../hero3-bg.jpg'
import { useLanguage } from '../LanguageProvider'

type Lang = 'tr' | 'en'

type Localized<T> = Record<Lang, T>

type Area = {
  title: Localized<string>
  description: Localized<string>
  icon: JSX.Element
  tags: Localized<string[]>
}

const AREAS: Area[] = [
  {
    title: {
      tr: 'Geliştirme Kartları',
      en: 'Development Boards',
    },
    description: {
      tr: 'Endüstriyel ve Ar-Ge projeleri için modüler, güvenilir ve ölçeklenebilir donanım geliştirme kartları.',
      en: 'Modular, reliable and scalable hardware development boards for industrial and R&D projects.',
    },
    icon: <Cpu className="text-primary" size={24} />,
    tags: {
      tr: ['Endüstriyel', 'Modüler', 'Ölçeklenebilir', 'IoT'],
      en: ['Industrial', 'Modular', 'Scalable', 'IoT'],
    },
  },
  {
    title: {
      tr: 'Güvenli Takip Teknolojileri',
      en: 'Secure Tracking Technologies',
    },
    description: {
      tr: 'Araç, ekipman ve personel için uçtan uca güvenli takip altyapıları ve bulut tabanlı raporlama.',
      en: 'End-to-end secure tracking infrastructure and cloud-based reporting for vehicles, equipment and personnel.',
    },
    icon: <ShieldCheck className="text-primary" size={24} />,
    tags: {
      tr: ['IoT', 'Bulut', 'Raporlama'],
      en: ['IoT', 'Cloud', 'Reporting'],
    },
  },
  {
    title: {
      tr: 'Geçiş Takip Sistemleri',
      en: 'Access Tracking Systems',
    },
    description: {
      tr: 'Kartlı/QR geçiş, yoklama ve yetkilendirme mekanizmalarıyla entegre erişim kontrol sistemleri.',
      en: 'Access control systems integrated with card/QR access, attendance and authorization mechanisms.',
    },
    icon: <DoorOpen className="text-primary" size={24} />,
    tags: {
      tr: ['RFID', 'QR', 'Yetkilendirme'],
      en: ['RFID', 'QR', 'Authorization'],
    },
  },
  {
    title: {
      tr: 'İHA Teknolojileri',
      en: 'UAV Technologies',
    },
    description: {
      tr: 'Otonom uçuş, görüntü işleme ve görev planlama altyapılarıyla endüstriyel İHA çözümleri geliştiriyoruz.',
      en: 'We develop industrial UAV solutions with autonomous flight, image processing and mission planning infrastructure.',
    },
    icon: <Plane className="text-primary" size={24} />,
    tags: {
      tr: ['Otonom Uçuş', 'Görüntü İşleme', 'Görev Planlama'],
      en: ['Autonomous Flight', 'Image Processing', 'Mission Planning'],
    },
  },
  {
    title: {
      tr: 'Denizcilik Teknolojileri',
      en: 'Maritime Technologies',
    },
    description: {
      tr: 'Açık deniz koşullarına uygun, konumlama ve telemetri tabanlı akıllı denizcilik çözümleri.',
      en: 'Smart maritime solutions based on positioning and telemetry, suitable for open sea conditions.',
    },
    icon: <Ship className="text-primary" size={24} />,
    tags: {
      tr: ['Konumlama', 'Telemetri', 'Dayanıklılık'],
      en: ['Positioning', 'Telemetry', 'Durability'],
  },
  },
  {
    title: {
      tr: 'Depo Takip Sistemleri',
      en: 'Warehouse Tracking Systems',
    },
    description: {
      tr: 'Forklift, raf ve stok seviyelerini anlık izleyen, verimlilik odaklı depo takip platformu.',
      en: 'A warehouse tracking platform focused on efficiency that monitors forklifts, shelves and stock levels in real time.',
    },
    icon: <Warehouse className="text-primary" size={24} />,
    tags: {
      tr: ['Forklift Takibi', 'Stok İzleme', 'Analitik'],
      en: ['Forklift Tracking', 'Stock Monitoring', 'Analytics'],
  },
  },
]

const HERO_TEXTS: Record<
  Lang,
  {
    title: string
    subtitle: string
  }
> = {
  tr: {
    title: 'Faaliyet Alanlarımız',
    subtitle: 'Temel çalışma alanlarımızı keşfedin. İleri donanım ve yazılım çözümlerini bir araya getiriyoruz.',
  },
  en: {
    title: 'Fields of Activity',
    subtitle: 'Discover our core fields of activity. We bring together advanced hardware and software solutions.',
  },
}

export function FaaliyetAlanlariPage() {
  const { language } = useLanguage()
  const getLocalized = <T,>(value: Localized<T>): T => value[language] ?? value.tr
  const hero = HERO_TEXTS[language] ?? HERO_TEXTS.tr
  const heroRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const $ = (window as any).jQuery as any
    const pg = (window as any).particleground as any
    if (!heroRef.current || !$.fn || !pg) return
    const $el = (window as any).jQuery(heroRef.current)
    if ($el.data('pg-init')) return
    $el.data('pg-init', true)
    $el.particleground({
      dotColor: 'rgba(93, 101, 107, 0.35)',
      lineColor: 'rgba(93, 101, 107, 0.35)',
      parallax: true,
      parallaxMultiplier: 50,
    })
    return () => {
      try { $el.particleground('destroy') } catch {}
    }
  }, [])

  return (
    <div>
      {/* Hero Section with particles */}
      <section className="relative py-16 pb-16 lg:pt-20 lg:pb-20 overflow-hidden">
        {/* Background image */}
        <div aria-hidden className="absolute inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: `url(${ctaBg})` }}></div>
        {/* Dark overlay */}
        <div aria-hidden className="absolute inset-0 z-[5] bg-black/45"></div>
        {/* Particles above overlay */}
        <div ref={heroRef} className="absolute inset-0 z-10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {hero.title}
            </h1>
            <p className="text-lg text-white max-w-3xl mx-auto">
              {hero.subtitle}
            </p>
          </div>
        </div>
      </section>

      <div className="py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {AREAS.map((area) => (
            <Card
              key={area.title.tr}
              className="group h-full relative overflow-hidden rounded-xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-gray-900/50 backdrop-blur-md shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-2 hover:bg-white/80 dark:hover:bg-gray-900/70 hover:ring-2 hover:ring-primary/40"
            >
              {/* premium gradient edge */}
              <div className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-gradient-to-br from-primary/15 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <CardContent className="relative z-10 p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-110 group-hover:bg-primary/15">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground transition-colors duration-300 group-hover:text-primary">
                    {getLocalized(area.title)}
                  </h3>
                </div>
                <p className="text-sm text-muted mb-4 transition-colors duration-300 group-hover:text-foreground">
                  {getLocalized(area.description)}
                </p>
                <div className="mt-auto flex flex-wrap gap-2">
                  {getLocalized(area.tags).map((t) => (
                    <Badge
                      key={t}
                      variant="outline"
                      className="text-xs backdrop-blur-sm bg-white/40 dark:bg-gray-800/40 border-white/30 dark:border-white/10 transition-all duration-300 group-hover:border-primary/50 group-hover:text-primary group-hover:bg-white/60 dark:group-hover:bg-gray-800/60"
                    >
                      {t}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}


